import { ApiError, deleteJSON, patchJSON, postJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type {
  MembershipLevelFutureRenewalWarning,
  MembershipLevelRosterCapWarning,
  MembershipLevel,
  MembershipLevelForm,
  MembershipLevelResponse,
  MembershipLevelsResponse,
} from '@/pages/MembershipLevelsPage.types'

export function createMembershipLevel(form: MembershipLevelForm) {
  return postJSON<MembershipLevelResponse>('/api/v1/membership_levels', {
    membership_level: membershipLevelPayload(form),
  })
}

export function updateMembershipLevel(
  level: MembershipLevel,
  form: MembershipLevelForm,
  options: { confirmFutureOnlyChange?: boolean; confirmRosterCapChange?: boolean } = {},
) {
  return patchJSON<MembershipLevelResponse>(`/api/v1/membership_levels/${level.id}`, {
    membership_level: membershipLevelPayload(form),
    ...(options.confirmFutureOnlyChange ? { confirm_future_only_change: true } : {}),
    ...(options.confirmRosterCapChange ? { confirm_roster_cap_change: true } : {}),
  })
}

export function updateMembershipLevelRenewalSettings(
  level: MembershipLevel,
  settings: {
    renewal_grace_days_override: number | null
    renewal_reminder_touches_override: Array<{ id?: string; offset: number }> | null
  },
) {
  return patchJSON<MembershipLevelResponse>(`/api/v1/membership_levels/${level.id}`, {
    membership_level: settings,
  })
}

export function archiveMembershipLevel(level: MembershipLevel) {
  return patchJSON<MembershipLevelResponse>(`/api/v1/membership_levels/${level.id}/archive`, {})
}

export function restoreMembershipLevel(level: MembershipLevel) {
  return patchJSON<MembershipLevelResponse>(`/api/v1/membership_levels/${level.id}/restore`, {})
}

export function deleteMembershipLevel(level: MembershipLevel) {
  return deleteJSON(`/api/v1/membership_levels/${level.id}`)
}

export function reorderMembershipLevels(levels: MembershipLevel[]) {
  return patchJSON<MembershipLevelsResponse>('/api/v1/membership_levels/reorder', {
    membership_level_ids: levels.map((level) => level.id),
  })
}

export function membershipLevelActionError(error: Error) {
  return apiFieldMessages(error, 'membership_level_order')[0] ?? error.message
}

export function membershipLevelFutureRenewalWarning(
  error: Error,
): MembershipLevelFutureRenewalWarning | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null
  if (!hasFutureRenewalWarning(error.body)) return null

  return error.body.future_renewal_warning
}

export function membershipLevelRosterCapWarning(
  error: Error,
): MembershipLevelRosterCapWarning | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null
  if (!hasRosterCapWarning(error.body)) return null

  return error.body.roster_cap_warning
}

function membershipLevelPayload(form: MembershipLevelForm) {
  return {
    name: form.name,
    kind: form.kind,
    roster_cap: form.kind === 'organization' && form.rosterCap ? Number(form.rosterCap) : null,
    description: form.description,
    fee: form.fee,
    renewal_structure: form.renewalStructure,
    renewal_term: form.renewalStructure === 'Lifetime' ? null : form.renewalTerm,
    fixed_renewal_date: form.renewalStructure === 'Fixed date' ? form.fixedRenewalDate : null,
    published_to_join_form: form.publishedToJoinForm,
    requires_admin_approval: form.requiresAdminApproval,
    prorate_first_partial_period:
      form.renewalStructure === 'Fixed date' ? form.prorateFirstPartialPeriod : false,
    tax_rate_id: form.taxRateId ? Number(form.taxRateId) : null,
    level_change_target_ids: form.levelChangeTargetIds,
  }
}

function hasFutureRenewalWarning(
  body: unknown,
): body is { future_renewal_warning: MembershipLevelFutureRenewalWarning } {
  if (typeof body !== 'object' || body === null || !('future_renewal_warning' in body)) return false

  const warning = (body as { future_renewal_warning: unknown }).future_renewal_warning
  return (
    typeof warning === 'object' &&
    warning !== null &&
    typeof (warning as { member_count?: unknown }).member_count === 'number' &&
    typeof (warning as { message?: unknown }).message === 'string'
  )
}

function hasRosterCapWarning(
  body: unknown,
): body is { roster_cap_warning: MembershipLevelRosterCapWarning } {
  if (typeof body !== 'object' || body === null || !('roster_cap_warning' in body)) return false

  const warning = (body as { roster_cap_warning: unknown }).roster_cap_warning
  return (
    typeof warning === 'object' &&
    warning !== null &&
    typeof (warning as { new_cap?: unknown }).new_cap === 'number' &&
    Array.isArray((warning as { organizations?: unknown }).organizations)
  )
}
